import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const MinimalTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white px-3 py-2 rounded border border-gray-100 shadow-lg z-50">
        <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">
          {payload[0].payload.name}
        </p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: entry.color || entry.stroke }}
            />
            <p className="text-sm font-bold text-gray-900">
              {entry.name}: {entry.value.toLocaleString()}
            </p>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const ChartCard = ({ title, children, footerData, isLoading }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-5 flex flex-col h-full shadow-sm">
    <div className="mb-4">
      <h3 className="text-sm font-bold text-gray-700">{title}</h3>
    </div>
    <div className="flex-1 min-h-[180px] w-full relative">
      {isLoading ? (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-400">
          Loading data...
        </div>
      ) : !children || children.props?.data?.length === 0 ? (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-300">
          No data available
        </div>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      )}
    </div>
    {footerData && (
      <div className="mt-4 pt-3 border-t border-gray-50 flex flex-wrap gap-3 justify-center">
        {footerData.map((item, idx) => (
          <div key={idx} className="flex items-center gap-1.5">
            <span
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: item.color }}
            />
            <span className="text-[10px] font-semibold text-gray-500 uppercase">
              {item.name}
            </span>
            <span className="text-[10px] font-bold text-gray-800">
              {item.value}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default function AdminAnalytics({ dashboardData, isLoading }) {
  const summary = dashboardData?.pipeline;
  const trends = dashboardData?.trends;
  const demographics = dashboardData?.demographics;
  const totalVacancies = dashboardData?.vacancies?.total;
  const openVacancies = dashboardData?.vacancies?.open;

  const pipelineData = summary
    ? [
      { name: "Total", value: summary.total },
      { name: "Verified", value: summary.verified },
      { name: "Approved", value: summary.approved },
      { name: "Accepted", value: summary.accepted },
    ]
    : [];

  const programData =
    demographics?.programs?.map((p, index) => ({
      ...p,
      color: ["#10b981", "#22d3ee", "#fbbf24", "#f472b6"][index % 4],
    })) || [];

  const vacancyData = [
    { name: "Open", value: openVacancies || 0, color: "#10b981" },
    {
      name: "Closed",
      value: (totalVacancies || 0) - (openVacancies || 0),
      color: "#e5e7eb",
    },
  ];

  const axisStyle = { fill: "#9ca3af", fontSize: 10, fontWeight: 500 };
  const gridStyle = { stroke: "#f3f4f6", strokeDasharray: "3 3" };

  return (
    <div className="space-y-6 pb-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChartCard title="Application Trends" isLoading={isLoading}>
          <LineChart
            data={trends}
            margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
          >
            <CartesianGrid {...gridStyle} vertical={false} />
            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={axisStyle}
              dy={10}
            />
            <YAxis axisLine={false} tickLine={false} tick={axisStyle} />
            <Tooltip content={<MinimalTooltip />} />
            <Line
              type="monotone"
              dataKey="total"
              name="New Apps"
              stroke="#cbd5e1"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="accepted"
              name="Accepted"
              stroke="#10b981"
              strokeWidth={2}
              dot={{ fill: "#fff", stroke: "#10b981", strokeWidth: 2, r: 3 }}
            />
          </LineChart>
        </ChartCard>

        <ChartCard title="Pipeline Overview" isLoading={isLoading}>
          <BarChart
            data={pipelineData}
            margin={{ top: 10, right: 0, left: -20, bottom: 0 }}
          >
            <CartesianGrid {...gridStyle} vertical={false} />
            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={axisStyle}
              dy={10}
            />
            <YAxis axisLine={false} tickLine={false} tick={axisStyle} />
            <Tooltip
              content={<MinimalTooltip />}
              cursor={{ fill: "transparent" }}
            />
            <Bar
              dataKey="value"
              fill="#10b981"
              radius={[3, 3, 0, 0]}
              barSize={40}
            />
          </BarChart>
        </ChartCard>

        <ChartCard
          title="Program Distribution"
          isLoading={isLoading}
          footerData={programData}
        >
          <PieChart>
            <Pie
              data={programData}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={70}
              paddingAngle={4}
              dataKey="value"
              stroke="none"
            >
              {programData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<MinimalTooltip />} />
            <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle">
              <tspan
                x="50%"
                dy="-0.5em"
                fontSize="20"
                fontWeight="bold"
                fill="#1f2937"
              >
                {programData.reduce((a, b) => a + b.value, 0)}
              </tspan>
              <tspan x="50%" dy="1.5em" fontSize="10" fill="#9ca3af">
                TOTAL
              </tspan>
            </text>
          </PieChart>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChartCard title="Vacancy Status" footerData={vacancyData}>
          <PieChart>
            <Pie
              data={vacancyData}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={70}
              paddingAngle={2}
              dataKey="value"
              stroke="none"
            >
              {vacancyData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<MinimalTooltip />} />
          </PieChart>
        </ChartCard>
        <div className="lg:col-span-2 bg-emerald-700 rounded-lg p-6 text-white flex flex-col justify-center items-center">
          <h3 className="text-lg font-medium opacity-80 mb-2">
            Total Interns Deployed
          </h3>
          <div className="text-5xl font-bold mb-4">
            {summary?.deployed || 0}
          </div>
          <p className="text-sm opacity-60 max-w-md text-center">
            Active interns currently placed in various departments across the
            selected period.
          </p>
        </div>
      </div>
    </div>
  );
}
